import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

const slides = [
  {
    id: 1,
    image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1800&q=90',
    eyebrow: 'Discover the World',
    heading: 'Plan Your Journey With Us',
    subtitle: 'International flight bookings at the best fares, crafted around your schedule.',
    cta1: { label: 'Contact Us', to: '/contact' },
    cta2: { label: 'Book Flights', to: '/services/flight-booking' }
  },
  {
    id: 2,
    image: 'https://images.pexels.com/photos/4931333/pexels-photo-4931333.jpeg',
    eyebrow: 'Luxury Stays',
    heading: 'Every Holiday\nDeserves Perfection',
    subtitle: 'Curated holiday packages tailored for truly unforgettable experiences.',
    cta1: { label: 'Contact Us', to: '/contact' },
    cta2: { label: 'View Packages', to: '/services/holiday-packages' }
  },
  {
    id: 3,
    image: 'https://i.pinimg.com/736x/ab/50/b9/ab50b941d83eab1b6172596168f62181.jpg',
    eyebrow: 'Hassle-Free Visas',
    heading: 'Visa Assistance\nMade Simple',
    subtitle: 'End-to-end documentation guidance from application to approval.',
    cta1: { label: 'Contact Us', to: '/contact' },
    cta2: { label: 'Visa Help', to: '/services/visa-assistance' }
  },
  {
    id: 4,
    image: 'https://i.pinimg.com/1200x/95/60/17/956017f8fb62537e1ce14ba83a657a0b.jpg',
    eyebrow: 'Corporate Travel',
    heading: 'Business Travel\nWithout Limits',
    subtitle: 'Reliable travel management for professionals and enterprises of every size.',
    cta1: { label: 'Contact Us', to: '/contact' },
    cta2: { label: 'Corporate Services', to: '/services/corporate-travel-services' }
  },
  {
    id: 5,
    image: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1800&q=90',
    eyebrow: 'Family Adventures',
    heading: 'Travel Together\nTravel Better',
    subtitle: 'Creating memorable journeys for families and groups across the globe.',
    cta1: { label: 'Contact Us', to: '/contact' },
    cta2: { label: 'Explore Destinations', to: '/destinations' }
  }
];

const SLIDE_DURATION = 6000;

function ArrowBtn({ onClick, label, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      aria-label={label}
      onMouseEnter={function() { setHovered(true); }}
      onMouseLeave={function() { setHovered(false); }}
      style={{
        width: 44, height: 44, borderRadius: '50%',
        border: '1.5px solid rgba(255,255,255,0.3)',
        background: hovered ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)',
        backdropFilter: 'blur(10px)',
        color: '#fff', fontSize: '1rem',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.2s, transform 0.2s',
        transform: hovered ? 'scale(1.1)' : 'scale(1)',
        flexShrink: 0
      }}
    >
      {children}
    </button>
  );
}

export default function HeroSlider() {
  const [current, setCurrent]         = useState(0);
  const [prev, setPrev]               = useState(null);
  const [textVisible, setTextVisible] = useState(true);
  const [progress, setProgress]       = useState(0);
  const timerRef  = useRef(null);
  const rafRef    = useRef(null);
  const startRef  = useRef(null);

  const goTo = useCallback(function(index) {
    if (index === current) return;
    setTextVisible(false);
    setPrev(current);
    setTimeout(function() {
      setCurrent(index);
      setPrev(null);
      setTextVisible(true);
      setProgress(0);
      startRef.current = performance.now();
    }, 600);
  }, [current]);

  const next = useCallback(function() { goTo((current + 1) % slides.length); }, [current, goTo]);
  const prev2 = useCallback(function() { goTo((current - 1 + slides.length) % slides.length); }, [current, goTo]);

  useEffect(function() {
    startRef.current = performance.now();
    setProgress(0);

    function tick(now) {
      var elapsed = now - startRef.current;
      setProgress(Math.min((elapsed / SLIDE_DURATION) * 100, 100));
      if (elapsed < SLIDE_DURATION) {
        rafRef.current = requestAnimationFrame(tick);
      }
    }
    rafRef.current = requestAnimationFrame(tick);

    timerRef.current = setTimeout(function() {
      setTextVisible(false);
      setPrev(current);
      setTimeout(function() {
        var n = (current + 1) % slides.length;
        setCurrent(n);
        setPrev(null);
        setTextVisible(true);
        setProgress(0);
        startRef.current = performance.now();
      }, 600);
    }, SLIDE_DURATION);

    return function() {
      clearTimeout(timerRef.current);
      cancelAnimationFrame(rafRef.current);
    };
  }, [current]);

  var slide = slides[current];

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      overflow: 'hidden',
      background: '#0a1a2e'
    }} className="hero-slider-wrap">

      {/* BACKGROUND IMAGES */}
      {slides.map(function(s, i) {
        var isCurrent  = i === current;
        var isPrevious = i === prev;
        if (!isCurrent && !isPrevious) return null;
        return (
          <div key={s.id} style={{
            position: 'absolute', inset: 0,
            opacity: isCurrent ? 1 : 0,
            transition: 'opacity 0.9s ease',
            zIndex: isCurrent ? 1 : 0
          }}>
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: 'url(' + s.image + ')',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              animation: isCurrent ? 'kenBurns 7s ease-out forwards' : 'none'
            }} />
          </div>
        );
      })}

      {/* GRADIENT OVERLAY */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 2,
        background: 'linear-gradient(90deg, rgba(17,45,78,0.88) 0%, rgba(17,45,78,0.65) 40%, rgba(17,45,78,0.25) 70%, rgba(17,45,78,0.05) 100%)'
      }} />

      {/* MOBILE extra bottom gradient so text is always readable */}
      <div className="hero-mobile-gradient" style={{
        position: 'absolute', inset: 0, zIndex: 2,
        background: 'linear-gradient(to top, rgba(17,45,78,0.7) 0%, transparent 50%)',
        display: 'none'
      }} />

      {/* CONTENT */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 3,
        display: 'flex', alignItems: 'center'
      }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 24px', width: '100%' }}>
          <div style={{ maxWidth: 600 }}>

            {/* Eyebrow */}
            <div style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s'
            }}>
              <span style={{
                display: 'inline-block',
                fontSize: '0.72rem', fontWeight: 700,
                letterSpacing: '0.2em', textTransform: 'uppercase',
                color: 'rgba(219,226,239,0.85)',
                marginBottom: 16, fontFamily: 'var(--font-body)'
              }}>
                {slide.eyebrow}
              </span>
            </div>

            {/* Heading */}
            <div style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.65s ease 0.25s, transform 0.65s ease 0.25s'
            }}>
              <h1 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(2rem, 6vw, 80px)',
                fontWeight: 600, color: '#ffffff',
                lineHeight: 1.1, margin: '0 0 18px',
                whiteSpace: 'pre-line',
                textShadow: '0 2px 20px rgba(0,0,0,0.2)'
              }}>
                {slide.heading}
              </h1>
            </div>

            {/* Subtitle */}
            <div style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.65s ease 0.4s, transform 0.65s ease 0.4s'
            }}>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'clamp(0.9rem, 2vw, 1.15rem)',
                fontWeight: 400, color: 'rgba(255,255,255,0.82)',
                lineHeight: 1.65, margin: '0 0 28px', maxWidth: 480
              }}>
                {slide.subtitle}
              </p>
            </div>

            {/* CTA Buttons */}
            <div style={{
              opacity: textVisible ? 1 : 0,
              transform: textVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.65s ease 0.55s, transform 0.65s ease 0.55s'
            }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
                <Link
                  to={slide.cta1.to}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '12px 24px',
                    background: 'var(--bj-clay)', color: '#fff',
                    borderRadius: 8, fontFamily: 'var(--font-body)',
                    fontWeight: 700, fontSize: '0.92rem',
                    textDecoration: 'none',
                    boxShadow: '0 4px 20px rgba(63,114,175,0.45)',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseEnter={function(e) { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={function(e) { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {slide.cta1.label}
                </Link>
                <Link
                  to={slide.cta2.to}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '12px 24px',
                    background: 'rgba(255,255,255,0.12)',
                    backdropFilter: 'blur(8px)',
                    color: '#fff', borderRadius: 8,
                    fontFamily: 'var(--font-body)', fontWeight: 700,
                    fontSize: '0.92rem', textDecoration: 'none',
                    border: '1.5px solid rgba(255,255,255,0.3)',
                    transition: 'background 0.2s, transform 0.2s'
                  }}
                  onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; }}
                  onMouseLeave={function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                >
                  {slide.cta2.label} →
                </Link>
              </div>

              {/* MOBILE ARROWS — own row, left and right edges */}
              <div className="hero-mobile-arrows" style={{
                display: 'none',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}>
                <ArrowBtn onClick={prev2} label="Previous slide">←</ArrowBtn>
                <ArrowBtn onClick={next}  label="Next slide">→</ArrowBtn>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DESKTOP SIDE ARROWS */}
      <button
        onClick={prev2}
        aria-label="Previous slide"
        className="hero-arrow-left"
        style={{
          position: 'absolute', left: 20, top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 4, width: 48, height: 48, borderRadius: '50%',
          border: '1.5px solid rgba(255,255,255,0.25)',
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
          color: '#fff', fontSize: '1rem', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s'
        }}
        onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; }}
        onMouseLeave={function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
      >←</button>

      <button
        onClick={next}
        aria-label="Next slide"
        className="hero-arrow-right"
        style={{
          position: 'absolute', right: 20, top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 4, width: 48, height: 48, borderRadius: '50%',
          border: '1.5px solid rgba(255,255,255,0.25)',
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
          color: '#fff', fontSize: '1rem', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s'
        }}
        onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; }}
        onMouseLeave={function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
      >→</button>

      {/* PROGRESS INDICATORS */}
      <div className="hero-progress" style={{
        position: 'absolute', bottom: 28, left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 4, display: 'flex', alignItems: 'center', gap: 8
      }}>
        {slides.map(function(s, i) {
          var isActive = i === current;
          return (
            <button
              key={s.id}
              onClick={function() { goTo(i); }}
              aria-label={'Go to slide ' + (i + 1)}
              style={{
                position: 'relative', height: 3,
                width: isActive ? 40 : 20,
                borderRadius: 2, border: 'none', padding: 0,
                cursor: 'pointer',
                background: 'rgba(255,255,255,0.3)',
                overflow: 'hidden',
                transition: 'width 0.4s cubic-bezier(.4,0,.2,1)'
              }}
            >
              {isActive && (
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: progress + '%',
                  background: '#fff', borderRadius: 2,
                  transition: 'width 0.1s linear'
                }} />
              )}
            </button>
          );
        })}
      </div>

      {/* FLOATING STAT CARD */}
      <div className="hero-stat-card" style={{
        position: 'absolute', bottom: 28, right: 24,
        zIndex: 4,
        background: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: 12, padding: '10px 18px',
        display: 'flex', alignItems: 'center', gap: 10
      }}>
        <span style={{ fontSize: '1.4rem' }}>✈</span>
        <div>
          <p style={{ margin: 0, fontWeight: 700, color: '#fff', fontSize: '1rem', lineHeight: 1 }}>25+</p>
          <p style={{ margin: '3px 0 0', color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', lineHeight: 1 }}>Countries Served</p>
        </div>
      </div>

      <style>{`
        @keyframes kenBurns {
          from { transform: scale(1); }
          to   { transform: scale(1.08); }
        }

        /* Desktop and tablet — full viewport */
        .hero-slider-wrap {
          height: 100vh;
          min-height: 560px;
        }

        /* Mobile — fixed comfortable height, not full viewport */
        @media (max-width: 768px) {
          .hero-slider-wrap {
            height: 580px;
            min-height: unset;
          }
          .hero-arrow-left,
          .hero-arrow-right { display: none !important; }
          .hero-mobile-arrows { display: flex !important; }
          .hero-mobile-gradient { display: block !important; }
          .hero-stat-card { display: none !important; }
          .hero-progress { bottom: 16px !important; }
        }

        @media (max-width: 480px) {
          .hero-slider-wrap {
            height: 520px;
          }
        }
      `}</style>
    </div>
  );
}